// batch-71 gesprek-secties (FO-537) - de gesprekscontrole: per gesprek een
// controlelijst voor het voertuig, de telefoon en het gereedschap van de medewerker,
// vooraf gevuld uit de app. Gedeelde DTO's en request-schemas (API en backoffice).
//
// KERNREGEL: een code (SIM-PIN, PUK, toestel-ontgrendelcode) staat nooit in een antwoord,
// DTO, PDF, verslag of log: alleen "geregistreerd: ja/nee". De code zelf gaat via een
// eigen route rechtstreeks naar de bron, versleuteld en met audit.

import { z } from 'zod'
import { queryBoolean } from './zod-query'
import type { VeiligheidsProcedureType } from './veiligheid'

// ─── Middelen en de instelling per gesprekstype ──────────────────────────────

export const GESPREK_CONTROLE_MIDDELEN = ['VOERTUIG', 'TELEFOON', 'GEREEDSCHAP', 'PBM'] as const
export type GesprekControleMiddel = (typeof GESPREK_CONTROLE_MIDDELEN)[number]

export const GESPREK_CONTROLE_MIDDEL_LABELS: Record<GesprekControleMiddel, string> = {
  VOERTUIG: 'Voertuig',
  TELEFOON: 'Telefoon',
  GEREEDSCHAP: 'Gereedschap',
  PBM: 'PBM',
}

/** Welk type een lijst per middel krijgt (de controlelijst-engine van Kwaliteit). */
export const GESPREK_CONTROLE_TYPE: Record<GesprekControleMiddel, VeiligheidsProcedureType> = {
  VOERTUIG: 'INSPECTIE_VOERTUIG',
  TELEFOON: 'CONTROLE_TELEFOON',
  GEREEDSCHAP: 'CONTROLELIJST_GEREEDSCHAPSKOFFER',
  PBM: 'INSPECTIE_PBM',
}

export const GESPREK_CONTROLE_MODI = ['UIT', 'OPTIONEEL', 'VERPLICHT'] as const
export type GesprekControleModus = (typeof GESPREK_CONTROLE_MODI)[number]

export const GESPREK_CONTROLE_MODUS_LABELS: Record<GesprekControleModus, string> = {
  UIT: 'Uit',
  OPTIONEEL: 'Optioneel',
  VERPLICHT: 'Verplicht',
}

/** `GesprekTypeConfig.controleLijsten`: per middel uit, optioneel of verplicht. */
export const gesprekControleLijstenSchema = z.object({
  VOERTUIG: z.enum(GESPREK_CONTROLE_MODI),
  TELEFOON: z.enum(GESPREK_CONTROLE_MODI),
  GEREEDSCHAP: z.enum(GESPREK_CONTROLE_MODI),
  PBM: z.enum(GESPREK_CONTROLE_MODI),
})
export type GesprekControleLijsten = z.infer<typeof gesprekControleLijstenSchema>

/** Zonder instelling op het gesprekstype (spec 237 A1). PBM alleen als iemand hem aanzet. */
export const GESPREK_CONTROLE_LIJSTEN_STANDAARD: GesprekControleLijsten = {
  VOERTUIG: 'OPTIONEEL',
  TELEFOON: 'OPTIONEEL',
  GEREEDSCHAP: 'OPTIONEEL',
  PBM: 'UIT',
}

/**
 * De middelen waarvoor de gesprekscontrole een lijst maakt. PBM heeft nog geen eigen sjabloon
 * met bindingen en geen terugschrijfpad: een PBM-lijst zou wel meetellen voor de vrijgeefregel,
 * maar nergens in te vullen zijn. Daarom staat PBM in deze batch altijd uit (review H2).
 */
export const GESPREK_CONTROLE_ACTIEVE_MIDDELEN = ['VOERTUIG', 'TELEFOON', 'GEREEDSCHAP'] as const satisfies readonly GesprekControleMiddel[]

/** Leest de JSON-kolom; een lege of onleesbare waarde is de standaard. PBM staat altijd uit. */
export function leesGesprekControleLijsten(waarde: unknown): GesprekControleLijsten {
  const r = gesprekControleLijstenSchema.safeParse(waarde)
  return { ...(r.success ? r.data : GESPREK_CONTROLE_LIJSTEN_STANDAARD), PBM: 'UIT' }
}

// ─── De bron-sleutels van de databinding (FO-537 §3.A2) ──────────────────────

/**
 * Elke `VeiligheidsChecklistItem.bronSleutel` komt uit deze lijst; de registry in
 * `apps/api/src/lib/controlelijst/bronnen.ts` moet ze allemaal hebben (compile-time) en
 * de drift-test `controlelijst-bronnen-drift.test.ts` toetst de seed-sjablonen.
 */
export const CONTROLE_BRON_SLEUTELS = [
  'voertuig.identiteit',
  'voertuig.km',
  'voertuig.staat_buiten',
  'voertuig.staat_binnen',
  'voertuig.foto',
  'voertuig.onderhoud',
  'voertuig.klachten',
  'voertuig.boekjes',
  'voertuig.kentekenbewijs',
  'voertuig.tankpas',
  'voertuig.sleutels',
  'voertuig.papieren',
  'telefoon.identiteit',
  'telefoon.staat',
  'telefoon.sim_aanwezig',
  'telefoon.iccid',
  'telefoon.nummer',
  'telefoon.sim_pin',
  'telefoon.toestelcode',
  'gereedschap.identiteit',
  'gereedschap.scan',
  'gereedschap.compleet',
  'gereedschap.staat',
  'gereedschap.keuringen',
  'algemeen.opmerkingen',
] as const
export type ControleBronSleutel = (typeof CONTROLE_BRON_SLEUTELS)[number]

export function isControleBronSleutel(s: string | null | undefined): s is ControleBronSleutel {
  return s != null && (CONTROLE_BRON_SLEUTELS as readonly string[]).includes(s)
}

/** De bronnen die alleen "ja/nee" tonen: nooit een tekst- of getalantwoord (codes). */
export const CONTROLE_GEHEIME_BRONNEN: readonly ControleBronSleutel[] = ['telefoon.sim_pin', 'telefoon.toestelcode']

/** Keuzelijst in Kwaliteit > Sjablonen (FO-537 §3.B7): sleutel plus leesbare naam. */
export const CONTROLE_BRON_LABELS: Record<ControleBronSleutel, string> = {
  'voertuig.identiteit': 'Voertuig: kenteken, merk en model, bestuurder sinds',
  'voertuig.km': 'Voertuig: km-stand (schrijft een km-registratie)',
  'voertuig.staat_buiten': 'Voertuig: staat buiten (schrijft het conditierapport)',
  'voertuig.staat_binnen': 'Voertuig: staat binnen (schrijft het conditierapport)',
  'voertuig.foto': 'Voertuig: foto voor het conditierapport',
  'voertuig.onderhoud': 'Voertuig: onderhoud en APK binnen interval',
  'voertuig.klachten': 'Voertuig: klachten (schrijft een voertuigmelding)',
  'voertuig.boekjes': 'Voertuig: boekjes (laatste antwoord)',
  'voertuig.kentekenbewijs': 'Voertuig: kentekenbewijs (werkt het voertuig bij)',
  'voertuig.tankpas': 'Voertuig: tankpas (signaal bij nee)',
  'voertuig.sleutels': 'Voertuig: sleutels (hoofd en reserve)',
  'voertuig.papieren': 'Voertuig: papieren (groene kaart, verklaringen)',
  'telefoon.identiteit': 'Telefoon: toestel, IMEI, serienummer en nummer',
  'telefoon.staat': 'Telefoon: staat van het toestel (reparatie-voorstel bij score 2 of lager)',
  'telefoon.sim_aanwezig': 'Telefoon: simkaart aanwezig',
  'telefoon.iccid': 'Telefoon: ICCID klopt (corrigeert de koppeling)',
  'telefoon.nummer': 'Telefoon: telefoonnummer klopt',
  'telefoon.sim_pin': 'Telefoon: SIM-PIN en PUK bekend (nooit de code zelf)',
  'telefoon.toestelcode': 'Telefoon: toestelcode vastgelegd (nooit de code zelf)',
  'gereedschap.identiteit': 'Gereedschap: koffer, uitgiftedatum en laatste controle',
  'gereedschap.scan': 'Gereedschap: QR-nummer van de koffer (wisselmelding bij verschil)',
  'gereedschap.compleet': 'Gereedschap: compleet volgens lijst (schrijft de dragercontrole)',
  'gereedschap.staat': 'Gereedschap: staat, orde en netheid (opmerking op de dragercontrole)',
  'gereedschap.keuringen': 'Gereedschap: keuringen binnen datum',
  'algemeen.opmerkingen': 'Algemeen: opmerkingen en afspraken (wordt een actiepunt)',
}

// ─── Waarden in de lijst ─────────────────────────────────────────────────────

/** Scores 1-5 voor staat binnen, buiten, koffer en telefoon (spec 237 A3). */
export const CONTROLE_SCORE_MIN = 1
export const CONTROLE_SCORE_MAX = 5
/** Een score tot en met deze waarde is een afwijking en vraagt een foto (spec 237 §6.6). */
export const CONTROLE_SCORE_AFWIJKING_TOT = 2
/** Een onderhoudsmoment dat binnen zoveel km of dagen valt telt als "bijna" (spec 237 T4). */
export const CONTROLE_ONDERHOUD_BIJNA_KM = 1000
export const CONTROLE_ONDERHOUD_BIJNA_DAGEN = 30
/** Het antwoord "niet van toepassing" op een JA_NEE_NVT-vraag. */
export const CONTROLE_ANTWOORD_NVT = 'N.v.t.'
/** Het antwoord "geen klachten" op de klachtenvraag. */
export const CONTROLE_KLACHT_GEEN = 'Geen'

export const CONTROLE_INHOUD_STATUSSEN = ['AANWEZIG', 'ONTBREEKT', 'KAPOT', 'NVT'] as const
export type ControleInhoudStatus = (typeof CONTROLE_INHOUD_STATUSSEN)[number]

export const CONTROLE_INHOUD_STATUS_LABELS: Record<ControleInhoudStatus, string> = {
  AANWEZIG: 'Aanwezig',
  ONTBREEKT: 'Ontbreekt',
  KAPOT: 'Kapot',
  NVT: 'N.v.t.',
}

/**
 * De status van een kofferregel in de vier waarden van de lijst. De drager kent er meer
 * (kwijt, in bestelling, versleten, vervangen): die tonen we als wat ze voor de controle zijn.
 */
export function controleInhoudStatusVan(dragerStatus: string): ControleInhoudStatus {
  switch (dragerStatus) {
    case 'AANWEZIG':
    case 'ONTBREEKT':
    case 'KAPOT':
    case 'NVT':
      return dragerStatus
    case 'KWIJT':
    case 'IN_BESTELLING':
    case 'NIET_ONTVANGEN':
      return 'ONTBREEKT'
    case 'VERSLETEN':
      return 'KAPOT'
    default:
      return 'AANWEZIG'
  }
}

/** Het gestructureerde antwoord op "compleet volgens lijst": de status per kofferregel. */
export const controleInhoudAntwoordSchema = z.object({
  regels: z
    .array(
      z.object({
        inhoudRegelId: z.string().trim().min(1).max(64),
        status: z.enum(CONTROLE_INHOUD_STATUSSEN),
        opmerking: z.string().trim().max(500).nullable().optional(),
      }),
    )
    .max(500),
})
export type ControleInhoudAntwoord = z.infer<typeof controleInhoudAntwoordSchema>

/** Eén antwoord in een concept-lijst. Nooit een code: geheime bronnen accepteren alleen ja/nee. */
export const gesprekControleAntwoordSchema = z.object({
  itemId: z.string().trim().min(1).max(64),
  antwoordBool: z.boolean().nullable().optional(),
  antwoordNum: z.number().min(-1).max(100_000_000).nullable().optional(),
  antwoordKeuze: z.string().trim().max(200).nullable().optional(),
  antwoordTekst: z.string().trim().max(4000).nullable().optional(),
  inhoud: controleInhoudAntwoordSchema.nullable().optional(),
})
export type GesprekControleAntwoordInput = z.infer<typeof gesprekControleAntwoordSchema>

/** Een kaart opslaan (CONCEPT): de antwoorden die veranderd zijn. */
export const gesprekControleOpslaanSchema = z.object({
  antwoorden: z.array(gesprekControleAntwoordSchema).max(200),
})
export type GesprekControleOpslaanInput = z.infer<typeof gesprekControleOpslaanSchema>

/** "Niet uitgevoerd" met reden, zichtbaar in de volgende voorbereiding (spec 237 §3.A5). */
export const gesprekControleNietUitgevoerdSchema = z.object({
  reden: z.string().trim().min(3, 'Geef de reden (minimaal 3 tekens)').max(1000, 'Maximaal 1000 tekens'),
})
export type GesprekControleNietUitgevoerdInput = z.infer<typeof gesprekControleNietUitgevoerdSchema>

export const GESPREK_CONTROLE_CODE_SOORTEN = ['TOESTELCODE', 'SIM_PIN', 'SIM_PUK'] as const
export type GesprekControleCodeSoort = (typeof GESPREK_CONTROLE_CODE_SOORTEN)[number]

export const GESPREK_CONTROLE_CODE_SOORT_LABELS: Record<GesprekControleCodeSoort, string> = {
  TOESTELCODE: 'Toestel-ontgrendelcode',
  SIM_PIN: 'SIM-PIN',
  SIM_PUK: 'PUK',
}

/** Een code invoeren: versleuteld op het uniek item, met audit. Komt nooit terug in een antwoord. */
export const gesprekControleCodeSchema = z.object({
  soort: z.enum(GESPREK_CONTROLE_CODE_SOORTEN),
  code: z.string().trim().min(4, 'Minimaal 4 tekens').max(64, 'Maximaal 64 tekens'),
})
export type GesprekControleCodeInput = z.infer<typeof gesprekControleCodeSchema>

// ─── DTO's ───────────────────────────────────────────────────────────────────

export type ControleSignaal = 'OK' | 'BIJNA' | 'TE_LAAT' | 'ONBEKEND'

/** Wat de app al weet over een vraag: de gebruiker bevestigt, corrigeert of vult aan. */
export interface ControlePrefillDTO {
  /** Wat er op dit moment bekend is, leesbaar ("48.210 km"). */
  tekst: string
  /** Waar het vandaan komt ("op 20-10-2026 via Telematica"), of null. */
  bron: string | null
  signaal: ControleSignaal | null
  /** Voorstel voor het antwoord. */
  antwoordBool: boolean | null
  antwoordNum: number | null
  antwoordKeuze: string | null
  /** Link naar de plek waar de opvolging gebeurt (afspraak plannen, koppeling corrigeren). */
  actie: { label: string; href: string } | null
}

/** De tekst bij een vraag waarvan de bron niet te lezen is (zie `controlePrefillBronOnbekend`). */
export const CONTROLE_BRON_ONBEKEND_TEKST = 'Niet te lezen uit de bron, vul het zelf in'

/**
 * batch-71 review (RUNBOOK: niets stil) - de prefill als een bron faalt of de registry de
 * `bronSleutel` niet (meer) kent. Zichtbaar `ONBEKEND` en bewust geen voorstel voor het antwoord:
 * wie de vraag beantwoordt, moet zelf kijken. Tot de review werd de prefill dan `null`, toonde de
 * kaart niets en vinkte de teamleider "ja" aan zonder gegevens; een onbekende bron mag een
 * afronding nooit stil groen maken.
 */
export function controlePrefillBronOnbekend(): ControlePrefillDTO {
  return {
    tekst: CONTROLE_BRON_ONBEKEND_TEKST,
    bron: null,
    signaal: 'ONBEKEND',
    antwoordBool: null,
    antwoordNum: null,
    antwoordKeuze: null,
    actie: null,
  }
}

export interface ControleInhoudRegelDTO {
  inhoudRegelId: string
  ladeNummer: number
  omschrijving: string
  aantal: number
  /** De huidige status op de drager. */
  status: string
}

export interface ControleAntwoordDTO {
  antwoordBool: boolean | null
  antwoordNum: number | null
  antwoordKeuze: string | null
  antwoordTekst: string | null
  heeftFoto: boolean
  inhoud: ControleInhoudAntwoord | null
}

export interface ControleVraagDTO {
  itemId: string
  volgorde: number
  vraag: string
  type: 'JA_NEE' | 'JA_NEE_NVT' | 'TEKST' | 'NUMERIEK' | 'FOTO' | 'HANDTEKENING' | 'SCORE' | 'KEUZE'
  verplicht: boolean
  risicoBijNee: boolean
  sectie: string | null
  opties: string[] | null
  scoreMin: number | null
  scoreMax: number | null
  helpTekst: string | null
  bronSleutel: ControleBronSleutel | null
  /** Antwoord alleen ja/nee: de code gaat via de eigen route (`magCodeInvoeren`). */
  geheim: boolean
  prefill: ControlePrefillDTO | null
  antwoord: ControleAntwoordDTO | null
  /** Bij "compleet volgens lijst": de verwachte inhoud per lade. */
  inhoud: ControleInhoudRegelDTO[] | null
}

export const CONTROLE_HOUDERS = ['WAGENPARK', 'MAGAZIJN', 'MEDEWERKER'] as const
export type ControleHouder = (typeof CONTROLE_HOUDERS)[number]

export const CONTROLE_HOUDER_LABELS: Record<ControleHouder, string> = {
  WAGENPARK: 'Wagenpark',
  MAGAZIJN: 'Magazijn',
  MEDEWERKER: 'Medewerker',
}

/** Eén afwijking (of afspraak) uit een lijst: wordt een actiepunt op het verslag. */
export interface ControleAfwijkingDTO {
  /** Stabiel binnen de lijst: het item, plus eventueel een deelsleutel. */
  sleutel: string
  omschrijving: string
  /** AFSPRAAK = een opmerking uit het gesprek: wel een actiepunt, geen melding. */
  soort: 'AFWIJKING' | 'AFSPRAAK'
  houder: ControleHouder
}

/** Eén registratie die bij het afronden naar de bron is geschreven. Nooit een code. */
export interface ControleTerugschrijfRegelDTO {
  bron: ControleBronSleutel
  /** Bv. "Km-registratie 48.650 km" of "Toestelcode geregistreerd: ja". */
  omschrijving: string
  /** Verwijzing naar de geschreven rij (kale id), of null. */
  referentieId: string | null
  /** Voorstel dat een mens nog moet oppakken (afspraak plannen, reparatie aanvragen). */
  voorstel: { label: string; href: string } | null
  waarschuwing: string | null
}

export interface ControleTerugschrijfResultaatDTO {
  regels: ControleTerugschrijfRegelDTO[]
  afwijkingen: ControleAfwijkingDTO[]
}

export interface ControleLijstDTO {
  invullingId: string
  middel: GesprekControleMiddel
  type: VeiligheidsProcedureType
  templateNaam: string | null
  /** Kenteken, toestel of kofferregel: waar deze lijst over gaat. */
  label: string
  status: 'CONCEPT' | 'AFGEROND'
  verplicht: boolean
  afgerondOp: string | null
  ingevuldDoorNaam: string | null
  nietUitgevoerdReden: string | null
  nietUitgevoerdOp: string | null
  /** Open voor de bijlage op het verslag (same-origin proxy). */
  pdfDocumentId: string | null
  vragen: ControleVraagDTO[]
  /** Bij een afgeronde lijst: wat er is teruggeschreven. */
  resultaat: ControleTerugschrijfResultaatDTO | null
}

/** Dossier: een afgeronde gesprekscontrole met wat er is vastgelegd (FO-537 §3.B6). Nooit een code. */
export interface ControleAfgerondDTO {
  invullingId: string
  middel: GesprekControleMiddel
  label: string
  afgerondOp: string | null
  ingevuldDoorNaam: string | null
  hrGesprekId: string | null
  resultaat: ControleTerugschrijfResultaatDTO | null
}

export interface GesprekControleDTO {
  gesprekId: string | null
  employeeId: string
  employeeNaam: string
  lijsten: ControleLijstDTO[]
  /** Alleen in het dossier: de eerder afgeronde controles, nieuwste eerst. */
  afgerond: ControleAfgerondDTO[]
  /** Middelen zonder lijst omdat de medewerker er geen heeft ("n.v.t.", spec 237 §6.1). */
  nietVanToepassing: GesprekControleMiddel[]
  /** Staat er voor dit middel geen actief sjabloon? Dan zegt de kaart dat; nooit stil niets. */
  sjabloonOntbreekt: GesprekControleMiddel[]
  /** Geen medewerkerrecord om de lijst op te zetten: wie opent moet er een hebben. */
  geenInvuller: boolean
  magInvullen: boolean
  magCodeInvoeren: boolean
  /** De bestaande reveal-route (VOORRAAD of ASSETS bewerken); met audit. */
  magCodeInzien: boolean
}

/** Module-instelling; de houders zijn gebruikerslijsten (geen benoemde groepen). */
export const gesprekControleInstellingSchema = z.object({
  wagenparkUserIds: z.array(z.string().trim().min(1).max(64)).max(50),
  magazijnUserIds: z.array(z.string().trim().min(1).max(64)).max(50),
  achterstalligMaanden: z.number().int().min(1, 'Minimaal 1 maand').max(36, 'Maximaal 36 maanden'),
})
export type GesprekControleInstellingInput = z.infer<typeof gesprekControleInstellingSchema>

export interface GesprekControleInstellingDTO extends GesprekControleInstellingInput {
  gewijzigdOp: string | null
  /** Voor de pickers: id en naam van de gekozen gebruikers. */
  opties: Array<{ id: string; naam: string }>
}

// ─── HRM > Controlelijsten (FO-537 §3.B) ─────────────────────────────────────

export const hrmControlelijstenQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  zoek: z.string().trim().max(100).optional(),
  departmentId: z.string().trim().min(1).max(64).optional(),
  middel: z.enum(GESPREK_CONTROLE_MIDDELEN).optional(),
  /** yyyy-mm-dd: alleen controles vanaf deze dag. */
  van: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Kies een geldige datum').optional(),
  tot: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Kies een geldige datum').optional(),
  /** Alleen medewerkers zonder controle in de laatste n maanden (instelling). */
  achterstallig: queryBoolean().optional(),
})
export type HrmControlelijstenQuery = z.infer<typeof hrmControlelijstenQuerySchema>

export interface HrmControleMiddelStandDTO {
  middel: GesprekControleMiddel
  /** yyyy-mm-dd van de laatste afgeronde lijst, of null. */
  laatsteOp: string | null
  /** Laagste score (1-5) op de laatste lijst, of null. */
  score: number | null
  afwijkingen: number
  invullingId: string | null
}

export interface HrmControleRijDTO {
  employeeId: string
  naam: string
  functie: string | null
  afdeling: string | null
  standen: HrmControleMiddelStandDTO[]
  /** yyyy-mm-dd van de laatste afgeronde lijst over alle middelen. */
  laatsteControleOp: string | null
  achterstallig: boolean
  openActiepunten: number
  /** Laagste score per afgeronde lijst, oudste eerst (de trend). */
  trend: number[]
}

export interface HrmControleKpiDTO {
  medewerkers: number
  achterstallig: number
  openActiepunten: number
  afgerondInPeriode: number
}

export interface HrmControleOverzichtDTO {
  rijen: HrmControleRijDTO[]
  kpi: HrmControleKpiDTO
  achterstalligMaanden: number
}
